<template>
    <div ranking question :id="`question-${questionData.number}`" @keyup.enter="submitQuestion">
        <questionheader :questionData="questionData" />
        <div data-question-main>
            <div dragged-answers-container>
                <draggable 
                    data-dragged-answers
                    v-model="draggedAnswers" 
                    draggable=".listitem"
                    group="answers" 
                    @end="dragged" 
                    >
                        <div
                            v-for="(answer, index) in draggedAnswers"
                            :key="`answer-${index}`"
                            data-answer
                            class="listitem"
                            role="listitem"
                            :id="`question-${questionData.number}_answer-${answer.number}`"
                            :is-checked="answer.isChecked"
                            :disabled="!get.editingAllowed(questionData) || state.global.showLoader"
                        >
                            <label data-index data-answer-part>
                                <input
                                    :value="answer.isChecked ? index + 1 : 0"
                                    type="number"
                                    @blur="numberInputChanged(answer, index, $event, `selectedAnswer`)"
                                    @keydown.enter.stop.prevent="numberInputChanged(answer, index, $event, `selectedAnswer`)"
                                    @keydown="numberInputKeyDown"
                                    onfocus="this.select();"
                                    :id="`question-${questionData.number}number-${answer.number}`"
                                />
                            </label>
                            <label data-right-label>
                                <input
                                    type="checkbox"
                                    checked
                                    data-only-screenreader
                                    :required="answersStillRequired"
                                    :id="`question-${questionData.number}_check-${answer.number}`"
                                />
                                <div data-label data-answer-part>
                                    <p v-html="DOMPurify.sanitize(answer.label)" :id="`question-${questionData.number}_label-${answer.number}`"></p>
                                </div>
                            </label>
                            <div data-answer-actions>
                                <div data-drag-part tabindex="0" role="drag">
                                    <div data-drag-icon></div>
                                </div>
                                <button data-remove-item 
                                    :title="$store.getters.textResource(`rs_remove_answer`)" 
                                    @click="removeGivenAnswer(index)"
                                    @keyup.space.prevent="removeGivenAnswer(index)"
                                    tabindex="0">
                                    <font-awesome-icon 
                                        :icon="`trash`"
                                        aria-hidden="true"
                                        tabindex="-1"
                                    />
                                </button>
                            </div>
                        </div>
                </draggable>
                <div
                    v-if="answersGiven < 1"
                    slot="footer"
                    data-placeholder-drag-text
                    role="group"
                    :aria-label="$store.getters.textResource(`rs_move_items_from_the_list`)"
                    key="footer"
                    >
                    <p>{{ $store.getters.textResource(`rs_move_items_from_the_list`) }}</p>                  
                </div>
            </div>
            <div draggable-answers-container :has-answers="!allAnswersGiven ?? ``">
                <draggable v-model="draggableAnswers" @end="dragged" group="answers" data-draggable-answers>
                        <div
                            v-for="(answer, index) in draggableAnswers"
                            :key="`answer-${index}`"
                            data-answer
                            role="listitem"
                            :id="`question-${questionData.number}_answer-${answer.number}`"
                            :is-checked="answer.isChecked"
                            :disabled="!get.editingAllowed(questionData) || state.global.showLoader"
                        >
                            <label data-index data-answer-part>
                                <input
                                    :value="answer.isChecked ? index + 1 : 0"
                                    type="number"
                                    @blur="numberInputChanged(answer, index, $event, `draggableAnswer`)"
                                    @keydown.enter.stop.prevent="numberInputChanged(answer, index, $event, `draggableAnswer`)"
                                    @keydown="numberInputKeyDown"
                                    onfocus="this.select();"                                   
                                    :id="`question-${questionData.number}number-${answer.number}`"
                                />
                            </label>
                            <label data-right-label>
                                <input
                                    type="checkbox"
                                    data-only-screenreader
                                    @change="checkboxChanged(answer)"
                                    :id="`question-${questionData.number}_check-${answer.number}`"
                                />
                                <div data-label data-answer-part>
                                    <p v-html="DOMPurify.sanitize(answer.label)" :id="`question-${questionData.number}_label-${answer.number}`"></p>
                                </div>
                            </label>
                        </div>
                </draggable>
                <div
                    v-if="allAnswersGiven"
                    slot="footer"
                    data-placeholder-drag-text
                    role="group"
                    :aria-label="$store.getters.textResource(`rs_ranking_all_answers_selected`)"
                    key="footer"
                    >
                    <p>{{ $store.getters.textResource(`rs_ranking_all_answers_selected`) }}</p>
                </div>
            </div>
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import draggable from "vuedraggable";

export default {
    name: `ranking-question`,
    mixins: [mixins.defaultQuestion],
    inject: ['DOMPurify'],
    components: {
        draggable,
    },
    data() {
        return {
            draggableAnswers: [],
            draggedAnswers: [],
        }
    },
    computed: {
        allAnswersGiven: function() {
            return this.answersGiven === this.questionData.answers.length;
        },
        // The answers that are selected by the user
        answersGiven: function () {
            return this.questionData.answers.filter((answer) => answer.isChecked).length;
        },
        // Decide whether the options have to have a required attribute added to them. In this way we can trigger
        // the browser to consider the form to be invalid as long as the right amount of options hasn't been selected
        answersStillRequired: function () {
            // Is the amount of selected within both the upper and lower limits that are allowed?
            let maxAmtValid, minAmtValid;
            let maxRestrictValue = parseInt(this.restrictions.maxValue);
            let minRestrictValue = parseInt(this.restrictions.minValue);

            if (maxRestrictValue === -1) maxAmtValid = false;
            else maxAmtValid = this.answersGiven > maxRestrictValue;

            if (minRestrictValue === -1) minAmtValid = false;
            else minAmtValid = this.answersGiven < minRestrictValue;

            return minAmtValid || maxAmtValid;
        },
    },
    watch: {
        draggedAnswers: {
            handler() {
                this.questionData.answers.forEach(answer => {
                    this.draggedAnswers.some(da => da.id === answer.id) 
                    ? answer.isChecked = true 
                    : answer.isChecked = false;                    
                });
            },
            deep: true
        }
    },
    methods: {
        dragged: function () {
            this.updateIndexes();
        },
        removeGivenAnswer(index) {
            this.draggableAnswers.push(this.draggedAnswers[index])
            this.draggedAnswers.splice([index], 1);
            this.updateIndexes();
        },
        // Event for when answers are clicked
        checkboxChanged: function (clickedAnswer) {
            let clickedAnswerIndex = this.draggableAnswers.findIndex((answer) => answer.id === clickedAnswer.id);
            // Select or deselect the answer that is clicked and move it to the right position
            if (clickedAnswer.isChecked === false) {
                clickedAnswer.isChecked = true;

                this.draggedAnswers.push(this.draggableAnswers[clickedAnswerIndex])

                this.draggableAnswers.splice([clickedAnswerIndex], 1);
            } 

            this.updateIndexes();
        },
        numberInputChanged: function (clickedAnswer, index, e, typeOfAnswer) {

            const value = e.target.value;

            // We don't want empty input numbers
            if (value === `` || parseInt(value) < 1) {
                e.target.value = clickedAnswer.isChecked ? index + 1 : 0;
                return;
            }          

            if(typeOfAnswer === `selectedAnswer`) {       
                let clickedAnswerIndex = this.draggedAnswers.findIndex((answer) => answer.id === clickedAnswer.id);
                let lastIndex = this.draggedAnswers.map((answer) => answer.isChecked).lastIndexOf(true);

                let item = this.draggedAnswers.splice(clickedAnswerIndex, 1);

                // If there are no answers selected yet, the input will always be at the top
                if (lastIndex === -1) lastIndex = 0;
                // Make sure we don't have gaps between the answers that are selected. Numbers that are too high, will be corrected
                let newIndex = Math.min(lastIndex, value - 1);

                clickedAnswer.isChecked = true;

                this.draggedAnswers.splice(newIndex, 0, item[0]);
            }
            else if(typeOfAnswer === `draggableAnswer`) {
                let clickedAnswerIndex = this.draggableAnswers.findIndex((answer) => answer.id === clickedAnswer.id);
                clickedAnswer.isChecked = true;
                this.draggedAnswers.splice(value - 1, 0, this.draggableAnswers[clickedAnswerIndex]);         
                this.draggableAnswers.splice([clickedAnswerIndex], 1);
            }

            this.updateIndexes();
        },
        numberInputKeyDown: function (e) {
            // We don't want the letter e in our number inputs
            if (e.code === `KeyE`) e.preventDefault();
        },
        // The backend wants to know the order of the answers by explicitly stating the index in the openField
        updateIndexes: function () {
            this.questionData.answers.forEach((answer, index) => {
                if (answer.isChecked) answer.openField =  (index + 1).toString();//The openfield is expected to be a string
                else answer.openField = ``;
            });

            this.draggedAnswers.forEach(function(answer, index) {
                if (answer.isChecked) answer.openField = (index + 1).toString(); //The openfield is expected to be a string
                else answer.openField = "";
            });
            
            this.draggableAnswers.map(function(x) { 
                x.isChecked = false; 
                x.openField = "";
                return x
            });

        },
    },
    mounted: function () {
        this.questionData.answers.forEach(answer => {
            if (answer.isChecked) {
                this.draggedAnswers.push(answer);
            }
            else {
                this.draggableAnswers.push(answer);
            }
        });
    }
};
</script>

<style scoped>
@import "./../styles/variables.css";

[ranking] {
    [data-answer] {
        margin: 0;
        padding: 0 0 10px 0;
        align-items: stretch;

        &[disabled] {
            background: #e4e4e4;
            pointer-events: none;
            opacity: 0.5;
        }
    }

    [data-question-main] {
        display: flex;
        align-items: center;

        [data-draggable-answers] {
            width: 100%;
            padding: 30px 0 0 0;
            min-height: 40px;       
        }
        > span {
            max-width: 700px;
            width: 100%;
            flex-direction: column;
        }
        [dragged-answers-container] {
            border: 2px dotted #D6D6D6;
            border-radius: 8px;
            min-height: 160px;
            padding: 20px;       
            margin-bottom: 20px;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            [data-placeholder-drag-text] {
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 40px;
            }
        }
        [draggable-answers-container] {
            border: 2px dotted #D6D6D6;
            border-radius: 8px;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            &[has-answers] {
                border-top: 2px solid #D6D6D6;
                border: none;
                border-radius: 0;
            }   
            [data-placeholder-drag-text] {
                padding-bottom: 30px;
            }
        }
        [data-dragged-answers] {
            min-height: 40px;
            height: 100%;
            width: 100%;
        }
    }

    [data-answer-actions] {
        display: flex;
        align-items: center;
    }

    [data-drag-part] {
        cursor: grab;
        width: 44px;
        height: 44px;
        margin: 18px 0px 18px 0px;
        display: flex;
        align-items: center;
        justify-content: center;
        [data-drag-icon] {
            background: radial-gradient(circle at 5px 5px, #6d6d6d 1px, transparent 2px) 0 0 / 7px 7px;
            width: 16px;
            height: 23px;
            pointer-events: none;
        }
    }
    [data-remove-item] {
        cursor: pointer;
        height: 44px;
        width: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        appearance: none;
        border: 0;
        background: no-repeat;
        svg {
            height: 50px;
            width: 15px;
            pointer-events: none;
        }
    }   

    [data-right-label] {
        min-height: 60px;
        width: 100%;
        display: flex;
        &:last-of-type {
            margin-bottom: 0;
        }

        input[type="checkbox"] {
            width: auto;
        }

        p {
            font-size: 15px;
            color: #454848;
            margin: 0;
            font-weight: bold;
            pointer-events: none;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            -khtml-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
        }

        [data-label] {
            width: 100%;
            padding-left: 40px;
            padding: 20px;
            margin-left: 10px;
        }

        input[type="checkbox"]:focus ~ div {
            outline: 1px dashed #333;
        }
    }

    [data-answer-part] {
        background: #fff;
        color: #fff;
        display: flex;
        align-items: center;
        border-radius: 5px;
        border: 2px solid #454848;
        cursor: pointer;

        &:hover {
            transition: 0.4s;
            background: #ddd;
        }
    }

    [data-index] {
        justify-content: center;
        min-width: 60px;
        padding: 12px 20px;

        input[type="number"] {
            color: #333;
            text-align: center;
            background: transparent;
            width: 40px;
            padding: 8px 10px;
            border: 0;
            border-bottom: 1px dashed;
        }
    }

    [is-checked] {
        &.sortable-chosen.sortable-ghost {
            [data-answer-part] {
                background: #FFF;
                border: 2px solid #454848;
            input[type="number"] {
                color: #333;
            }
            p {
                color: #454848;
            }
            &:hover {
                background: #FFF;
            }
        }

        [data-drag-part],[data-remove-item] {
            display: none;
            }
        } 

        [data-answer-part] {
            background: var(--primary-color);
            color: #fff;
            border: 2px solid var(--primary-color);
            transition: 0.2s;

            p {
                color: #fff;
            }

            input[type="number"] {
                color: #fff;
            }
        }

        [data-answer-part] {
            &:hover {
                background: var(--primary-color);
                filter: brightness(115%);
            }
        }
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type="number"] {
        -moz-appearance: textfield;
    }
}
</style>